(() => {
  'use strict';

  if (window.ArchiveMemberRewards) return;

  const root = document.querySelector('[data-archive-member-rewards]');
  const supplied = window.archiveMemberRewardsConfig || {};

  if (!root || supplied.isAuthenticated) return;

  const dialog = root.querySelector('[role="dialog"]');
  const closeControls = root.querySelectorAll('[data-member-rewards-close]');
  const actionLinks = root.querySelectorAll('[data-member-rewards-action]');
  const authenticationLinks = root.querySelectorAll('[data-member-rewards-auth]');
  const SESSION_PREFIX = 'twa.memberRewards.session.';
  const LOCAL_PREFIX = 'twa.memberRewards.local.';
  const DAY = 24 * 60 * 60 * 1000;
  const memory = { session: {}, local: {} };

  const CONFIG = Object.freeze({
    dismissDays: Number(supplied.dismissDays || root.dataset.dismissDays || 7),
    eligiblePageDelay: 10000,
    postConflictDelay: 1800,
    addToCartIntentDuration: 8000,
    checkoutIntentDuration: 60000,
    authenticationIntentDuration: 10 * 60 * 1000,
    conflictSelectors: [
      'dialog[open]',
      'details[open][scroll-lock]',
      'header-drawer details.menu-open',
      '.agp__wrapper',
      '#agp__noPeekScreen',
      '.archive-review-modal--open',
      '.archive-clear-modal:not([hidden])',
      '.archive-rewards-confirm.is-open',
      '.archive-rewards-birthday.is-open',
      '.archive-rewards-gift-info.is-open',
      '.archive-milestone-modal.is-open',
    ],
  });

  let previousFocus = null;
  let restoreBody = null;
  let retryTimer = null;
  let hideTimer = null;
  let ageVerificationPollTimer = null;
  let eligiblePageStartedAt = null;
  const initialCartIcon = document.querySelector('[data-testid="cart-icon"]');
  let cartItemCount = initialCartIcon?.classList.contains('header-actions__cart-icon--has-cart') ? 1 : 0;
  let cartItemCountKnown = cartItemCount === 0;
  let cartHadItems = cartItemCount > 0;

  function storageFor(scope) {
    try {
      return scope === 'session' ? window.sessionStorage : window.localStorage;
    } catch (_error) {
      return null;
    }
  }

  function read(scope, key, fallback = null) {
    const storage = storageFor(scope);
    const fullKey = `${scope === 'session' ? SESSION_PREFIX : LOCAL_PREFIX}${key}`;
    try {
      const value = storage ? storage.getItem(fullKey) : memory[scope][key];
      return value === null || typeof value === 'undefined' ? fallback : JSON.parse(value);
    } catch (_error) {
      return fallback;
    }
  }

  function write(scope, key, value) {
    const storage = storageFor(scope);
    const fullKey = `${scope === 'session' ? SESSION_PREFIX : LOCAL_PREFIX}${key}`;
    try {
      if (storage) storage.setItem(fullKey, JSON.stringify(value));
      else memory[scope][key] = JSON.stringify(value);
    } catch (_error) {
      memory[scope][key] = JSON.stringify(value);
    }
  }

  function pathIsProtected() {
    const path = window.location.pathname.replace(/\/$/, '') || '/';
    return root.dataset.template === 'cart'
      || path.endsWith('/pages/archive-rewards')
      || /\/(cart|checkout|checkouts|account|customer_authentication|authentication|password|challenge)(\/|$)/.test(path);
  }

  function ageVerificationHasPassed() {
    const ageChecker = window.agecheckerVsARddsz9;
    const ageRoot = document.querySelector('#agp__root, #agp__noPeekScreen');
    if (!ageChecker && !ageRoot) return true;

    try {
      if (typeof ageChecker?.getHistory === 'function') {
        return ageChecker.getHistory() === 'pass';
      }
      if (typeof window.agechecker_developer_api?.context === 'function') {
        return window.agechecker_developer_api.context()?.history === 'pass';
      }
    } catch (_error) {
      return false;
    }

    return false;
  }

  function hasRecentCommerceIntent() {
    return cartItemCount > 0
      || Number(read('session', 'cartIntentUntil', 0)) > Date.now()
      || Number(read('session', 'checkoutIntentUntil', 0)) > Date.now();
  }

  function hasRecentAuthenticationIntent() {
    return Number(read('session', 'authenticationIntentUntil', 0)) > Date.now();
  }

  function isGenuinelyVisible(candidate) {
    if (!candidate || candidate.hidden || candidate.getAttribute('aria-hidden') === 'true') return false;
    const styles = window.getComputedStyle(candidate);
    const bounds = candidate.getBoundingClientRect();
    return styles.display !== 'none'
      && styles.visibility !== 'hidden'
      && styles.opacity !== '0'
      && styles.pointerEvents !== 'none'
      && bounds.width > 0
      && bounds.height > 0;
  }

  function blockingOverlayDetails() {
    if (!root.hidden) return null;

    for (const selector of CONFIG.conflictSelectors) {
      const candidate = document.querySelector(selector);
      if (candidate && !root.contains(candidate) && isGenuinelyVisible(candidate)) {
        return {
          reason: candidate.matches('[data-testid="cart-drawer-dialog"]') ? 'cart_drawer' : 'known_overlay',
          selector,
          tag: candidate.tagName.toLowerCase(),
        };
      }
    }

    if (document.documentElement.hasAttribute('scroll-lock')) {
      return { reason: 'scroll_lock', selector: 'html[scroll-lock]', tag: 'html' };
    }
    if (document.body.classList.contains('archive-review-modal-open')) {
      return { reason: 'archive_review', selector: 'body.archive-review-modal-open', tag: 'body' };
    }
    if (document.body.classList.contains('archive-rewards-modal-open')) {
      return { reason: 'archive_rewards', selector: 'body.archive-rewards-modal-open', tag: 'body' };
    }

    const roleDialog = [...document.querySelectorAll('[role="dialog"]')].find((candidate) => {
      return !root.contains(candidate) && isGenuinelyVisible(candidate);
    });
    if (roleDialog) {
      return { reason: 'role_dialog', selector: '[role="dialog"]', tag: roleDialog.tagName.toLowerCase() };
    }

    const openPopover = [...document.querySelectorAll('[popover]')].find((candidate) => {
      if (candidate.id === 'PBarNextFrameWrapper') return false;
      try {
        return candidate.matches(':popover-open');
      } catch (_error) {
        return false;
      }
    });
    if (openPopover) {
      return { reason: 'popover', selector: '[popover]:popover-open', tag: openPopover.tagName.toLowerCase() };
    }

    return null;
  }

  function hasConflict() {
    return Boolean(blockingOverlayDetails());
  }

  function isCooledDown() {
    return Number(read('local', 'dismissedUntil', 0)) > Date.now();
  }

  function autoEligibilityState() {
    const now = Date.now();
    const agePassed = ageVerificationHasPassed();
    const protectedRoute = pathIsProtected();

    if (!agePassed || protectedRoute) {
      eligiblePageStartedAt = null;
    } else if (eligiblePageStartedAt === null) {
      eligiblePageStartedAt = now;
    }

    const shownThisSession = Boolean(read('session', 'shown', false));
    const cooldownActive = isCooledDown();
    const authenticationIntent = hasRecentAuthenticationIntent();
    const cartSuppressionActive = cartItemCount > 0 || Number(read('session', 'cartIntentUntil', 0)) > now;
    const checkoutIntent = Number(read('session', 'checkoutIntentUntil', 0)) > now;
    const commerceIntent = hasRecentCommerceIntent();
    const blocker = blockingOverlayDetails();
    const blockingOverlay = Boolean(blocker);
    const eligiblePageAge = eligiblePageStartedAt === null ? 0 : now - eligiblePageStartedAt;
    const minimumDelayRemaining = Math.max(0, CONFIG.eligiblePageDelay - eligiblePageAge);

    let reason = 'eligible';
    if (!root.hidden) reason = 'already_open';
    else if (!agePassed) reason = 'age_not_passed';
    else if (protectedRoute) reason = 'protected_route';
    else if (shownThisSession) reason = 'already_shown';
    else if (cooldownActive) reason = 'dismissal_cooldown';
    else if (authenticationIntent) reason = 'authentication_intent';
    else if (minimumDelayRemaining > 0) reason = 'minimum_delay';
    else if (commerceIntent) reason = 'commerce_intent';
    else if (blockingOverlay) reason = 'blocking_overlay';

    return {
      eligible: reason === 'eligible',
      reason,
      agePassed,
      protectedRoute,
      shownThisSession,
      cooldownActive,
      authenticationIntent,
      commerceIntent,
      cartEmpty: cartItemCount === 0,
      cartItemCountKnown,
      cartSuppressionActive,
      checkoutIntent,
      blockingOverlay,
      blockingOverlayReason: blocker?.reason || null,
      blockingOverlaySelector: blocker?.selector || null,
      blockingOverlayTag: blocker?.tag || null,
      eligiblePageAge,
      minimumDelayRemaining,
    };
  }

  function canManualOpenNow() {
    if (!root.hidden) return { eligible: false, reason: 'already_open' };
    if (!ageVerificationHasPassed()) return { eligible: false, reason: 'age_not_passed' };
    if (hasConflict()) return { eligible: false, reason: 'blocking_overlay' };
    return { eligible: true, reason: 'eligible' };
  }

  function focusableElements() {
    return [...dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((element) => element.offsetParent !== null);
  }

  function lockScroll() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    restoreBody = {
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight,
    };
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    document.body.classList.add('archive-member-rewards-open');
  }

  function unlockScroll() {
    if (!restoreBody) return;
    document.body.style.overflow = restoreBody.overflow;
    document.body.style.paddingRight = restoreBody.paddingRight;
    document.body.classList.remove('archive-member-rewards-open');
    restoreBody = null;
  }

  function open(context = 'general', options = {}) {
    const manual = Boolean(options.manual);
    const eligibility = manual ? canManualOpenNow() : autoEligibilityState();
    if (!eligibility.eligible) {
      root.dispatchEvent(new CustomEvent('archive:member-rewards-blocked', {
        bubbles: true,
        detail: { context, manual, reason: eligibility.reason },
      }));
      return false;
    }

    window.clearTimeout(hideTimer);
    previousFocus = document.activeElement;
    if (!manual) write('session', 'shown', true);
    root.dataset.context = context;
    root.hidden = false;
    lockScroll();
    window.requestAnimationFrame(() => {
      root.classList.add('is-open');
      dialog.focus({ preventScroll: true });
    });
    root.dispatchEvent(new CustomEvent('archive:member-rewards-opened', {
      bubbles: true,
      detail: { context, manual },
    }));
    return true;
  }

  function close(reason = 'close') {
    if (root.hidden) return;
    const isDismissal = ['close', 'backdrop', 'escape'].includes(reason);
    if (isDismissal) write('local', 'dismissedUntil', Date.now() + CONFIG.dismissDays * DAY);
    root.classList.remove('is-open');
    unlockScroll();
    hideTimer = window.setTimeout(() => {
      root.hidden = true;
      root.removeAttribute('data-context');
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220);
    if (previousFocus && document.contains(previousFocus)) previousFocus.focus({ preventScroll: true });
    previousFocus = null;
    root.dispatchEvent(new CustomEvent('archive:member-rewards-closed', {
      bubbles: true,
      detail: { reason },
    }));
  }

  function scheduleEvaluation(delay = 0) {
    window.clearTimeout(retryTimer);
    const eligibility = autoEligibilityState();
    const cartDelay = Math.max(0, Number(read('session', 'cartIntentUntil', 0)) - Date.now());
    const commerceDelay = Math.max(0, Number(read('session', 'checkoutIntentUntil', 0)) - Date.now());
    const authenticationDelay = Math.max(0, Number(read('session', 'authenticationIntentUntil', 0)) - Date.now());
    if (
      eligibility.eligible
      || eligibility.reason === 'minimum_delay'
      || eligibility.reason === 'commerce_intent'
      || eligibility.reason === 'authentication_intent'
    ) {
      retryTimer = window.setTimeout(
        () => open('automatic'),
        Math.max(delay, eligibility.minimumDelayRemaining || 0, cartDelay, commerceDelay, authenticationDelay)
      );
    }
  }

  function configureAuthenticationLinks() {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    const authenticationUrl = `/customer_authentication/login?return_to=${encodeURIComponent(returnTo)}`;
    authenticationLinks.forEach((link) => link.setAttribute('href', authenticationUrl));
  }

  function markCartIntent(duration) {
    const until = Date.now() + duration;
    const existingUntil = Number(read('session', 'cartIntentUntil', 0));
    write('session', 'cartIntentUntil', Math.max(existingUntil, until));
    cartHadItems = true;
    window.clearTimeout(retryTimer);
  }

  function markCheckoutIntent() {
    const until = Date.now() + CONFIG.checkoutIntentDuration;
    const existingUntil = Number(read('session', 'checkoutIntentUntil', 0));
    write('session', 'checkoutIntentUntil', Math.max(existingUntil, until));
    window.clearTimeout(retryTimer);
  }

  function cartCountFromUpdate(event) {
    const resourceCount = Number(event.detail?.resource?.item_count);
    if (Number.isFinite(resourceCount)) return Math.max(0, resourceCount);

    const data = event.detail?.data;
    const eventCount = Number(data?.itemCount);
    if (!Number.isFinite(eventCount)) return null;
    if (data?.source === 'product-form-component') return Math.max(0, cartItemCount + eventCount);
    return Math.max(0, eventCount);
  }

  function updateCartState(event) {
    const nextCount = cartCountFromUpdate(event);
    if (nextCount === null) return;

    const transitionedToEmpty = cartHadItems && nextCount === 0;
    cartItemCount = nextCount;
    cartItemCountKnown = true;
    cartHadItems = nextCount > 0;
    if (!transitionedToEmpty) return;

    write('session', 'cartIntentUntil', 0);
    eligiblePageStartedAt = ageVerificationHasPassed() && !pathIsProtected() ? Date.now() : null;
    window.clearTimeout(retryTimer);
    scheduleEvaluation();
  }

  function isCheckoutAction(target) {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest([
      'a[href*="/checkout"]',
      'button[name="checkout"]',
      'input[name="checkout"]',
      '[data-testid="Checkout-button"]',
      'shopify-accelerated-checkout-cart',
      'shopify-accelerated-checkout',
      '.shopify-payment-button',
    ].join(',')));
  }

  function scheduleAfterBlockingUi() {
    window.setTimeout(() => scheduleEvaluation(CONFIG.postConflictDelay), 0);
  }

  function stopAgeVerificationPolling() {
    if (ageVerificationPollTimer === null) return;
    window.clearInterval(ageVerificationPollTimer);
    ageVerificationPollTimer = null;
  }

  function startAgeVerificationFlow() {
    if (ageVerificationHasPassed()) {
      stopAgeVerificationPolling();
      scheduleEvaluation();
      return;
    }

    if (ageVerificationPollTimer !== null) return;

    ageVerificationPollTimer = window.setInterval(() => {
      if (!ageVerificationHasPassed()) return;
      stopAgeVerificationPolling();
      scheduleEvaluation();
    }, 350);
  }

  function observeBlockingUiState() {
    if (typeof MutationObserver === 'undefined') return;

    const bodyStateObserver = new MutationObserver(() => {
      if (!hasConflict()) scheduleAfterBlockingUi();
    });
    bodyStateObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    const blockerStateObserver = new MutationObserver(() => {
      if (!hasConflict()) scheduleAfterBlockingUi();
    });
    document.querySelectorAll([
      '#archive-clear-watchlist-modal',
      '[data-review-modal]',
      '.archive-rewards-confirm',
      '.archive-rewards-birthday',
      '.archive-rewards-gift-info',
      '.archive-milestone-modal',
    ].join(',')).forEach((element) => {
      blockerStateObserver.observe(element, {
        attributes: true,
        attributeFilter: ['class', 'hidden'],
      });
    });
  }

  configureAuthenticationLinks();
  observeBlockingUiState();

  closeControls.forEach((control) => {
    control.addEventListener('click', () => close(control.classList.contains('archive-member-rewards__backdrop') ? 'backdrop' : 'close'));
  });

  actionLinks.forEach((link) => link.addEventListener('click', () => close('navigation')));

  authenticationLinks.forEach((link) => {
    link.addEventListener('click', () => {
      write('session', 'authenticationIntentUntil', Date.now() + CONFIG.authenticationIntentDuration);
    });
  });

  document.addEventListener('dialog:open', () => window.clearTimeout(retryTimer), true);
  document.addEventListener('dialog:close', scheduleAfterBlockingUi, true);
  document.addEventListener('close', scheduleAfterBlockingUi, true);

  document.addEventListener('toggle', (event) => {
    if (event.target instanceof HTMLDetailsElement && !event.target.open) {
      scheduleAfterBlockingUi();
    } else if (event.target instanceof HTMLElement && event.target.hasAttribute('popover')) {
      let isOpen = false;
      try {
        isOpen = event.target.matches(':popover-open');
      } catch (_error) {
        return;
      }
      if (!isOpen) scheduleAfterBlockingUi();
    }
  }, true);

  document.addEventListener('click', (event) => {
    if (isCheckoutAction(event.target)) markCheckoutIntent();
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    const submitter = event.submitter instanceof Element ? event.submitter : null;
    if (isCheckoutAction(submitter) || form?.action.includes('/checkout')) {
      markCheckoutIntent();
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (root.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close('escape');
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = focusableElements();
    if (!focusable.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('cart:update', (event) => {
    const data = event.detail?.data;
    if (data?.source === 'product-form-component' && !data.didError) {
      markCartIntent(CONFIG.addToCartIntentDuration);
      scheduleEvaluation(CONFIG.addToCartIntentDuration);
    }
    if (!data?.didError) updateCartState(event);
  }, true);

  document.addEventListener('archive:member-rewards-open', (event) => {
    open(event.detail?.context || 'manual', { manual: true });
  });

  window.ArchiveMemberRewards = Object.freeze({
    open: (context = 'manual') => open(context, { manual: true }),
    close,
    debug: () => Object.freeze({ ...autoEligibilityState() }),
    config: CONFIG,
  });

  startAgeVerificationFlow();
})();
